import { useRef, useState } from 'react';
import { Box, Flex, Grid } from '@chakra-ui/react';

import { FCWithChildren } from 'common/types';
import Button from 'components/_core/Button';

import Item from './components/Item';
import Context from './Context';
import locales from './locales';

interface TopBarProps {
  onClose: () => void;
  nbPlans: number;
  expanded: boolean;
  content?: React.ReactElement | null;
}

/**
 * This component is used to create the recap top bar on the pricing page.
 * Every card is injected using TopBar.Item
 */
const TopBar: FCWithChildren<TopBarProps> = ({
  children,
  content,
  expanded,
  nbPlans,
  onClose,
}) => {
  const containerRef = useRef(null);
  const [x, setX] = useState(0);
  const width = nbPlans * 292 + (nbPlans - 1) * 32;

  return (
    <Context.Provider
      value={{
        containerRef,
        onPositionChange: setX,
      }}
    >
      <Box
        _after={{
          backdropFilter: `${expanded ? 'blur(16px)' : 'transparent'}`,
          backgroundColor: `${expanded ? 'rgba(255, 255, 255, 0.8)' : 'white'}`,
          borderBottom: '1px solid',
          borderColor: 'grey.4',
          content: '""',
          height: 'full',
          left: 0,
          position: 'absolute',
          top: 0,
          transform: `translateY(${expanded ? 0 : 'calc(-100% + 4rem)'})`,
          transition: expanded
            ? 'transform 250ms cubic-bezier(0.4, 0.0, 1, 1), opacity 250ms cubic-bezier(0.4, 0.0, 1, 1)'
            : 'transform 200ms cubic-bezier(0.0, 0.0, 0.2, 1), opacity 200ms cubic-bezier(0.0, 0.0, 0.2, 1)',
          width: 'full',
          zIndex: -2,
        }}
        height={expanded ? 'auto' : '4rem'}
        justifyContent="space-between"
        left={0}
        position="fixed"
        top={0}
        width="100vw"
        zIndex={2}
      >
        <Box left="1.5rem" position="absolute" top="1rem">
          <Button
            icon="cross"
            iconPosition="left"
            onClick={onClose}
            size="sm"
            variant="tertiary"
          >
            {locales.closeButtonText}
          </Button>
        </Box>
        {content ? (
          <Flex
            alignItems="center"
            height="4rem"
            left={`${x}px`}
            position="fixed"
            top={0}
            width={`${width}px}`}
            zIndex={2}
          >
            {content}
          </Flex>
        ) : null}
        <Box
          marginLeft={`${x}px`}
          paddingTop={content ? '4rem' : 0}
          transform={`translateY(${expanded ? 0 : '-100%'})`}
          transition={
            expanded
              ? 'transform 250ms cubic-bezier(0.4, 0.0, 1, 1), opacity 250ms cubic-bezier(0.4, 0.0, 1, 1)'
              : 'transform 200ms cubic-bezier(0.0, 0.0, 0.2, 1), opacity 200ms cubic-bezier(0.0, 0.0, 0.2, 1)'
          }
        >
          <Grid
            gap="space-40"
            gridTemplateColumns={`repeat(${nbPlans}, 1fr)`}
            ref={containerRef}
            width={`${width}px}`}
          />
        </Box>
      </Box>
      {children}
    </Context.Provider>
  );
};

export default Object.assign(TopBar, {
  Item,
});
